<script lang="ts">
import { defineComponent, PropType, computed, onMounted } from 'vue'
import InputForm from '@/components/form/InputForm.vue'
import InputFormPhoneNumber from '@/components/lead/InputFormPhoneNumber.vue'
import InputCompanyName from '@/components/lead/InputCompanyName.vue'
import InputFormDepartmentAndPosition from '@/components/lead/InputFormDepartmentAndPosition.vue'
import InputFormWorkMailAddress from '@/components/lead/InputFormWorkMailAddress.vue'
import SelectorIndustry from '@/components/lead/SelectorIndustry.vue'
import SelectorPositionLevel from '@/components/lead/SelectorPositionLevel.vue'
import SelectorProfession from '@/components/lead/SelectorProfession.vue'
import SelectorProfessionWithDomain from '@/components/lead/SelectorProfessionWithDomain.vue'
import SelectorScale from '@/components/lead/SelectorScale.vue'
import SelectorListingStatus from './SelectorListingStatus.vue'
import { isValidNumber } from '@/utilities/validator'

export default defineComponent({
  name: 'InputProfileCareer',

  components: {
    InputForm,
    InputFormPhoneNumber,
    InputCompanyName,
    InputFormDepartmentAndPosition,
    InputFormWorkMailAddress,
    SelectorIndustry,
    SelectorPositionLevel,
    SelectorProfession,
    SelectorProfessionWithDomain,
    SelectorScale,
    SelectorListingStatus,
  },

  props: {
    namespace: {
      type: String,
      default: '',
    },

    userLeadInfo: {
      type: Object as PropType<UserLeadInfo>,
      required: true,
    },

    /**
     * 住所入力欄を表示するかどうか
     *
     * @description 表示しない場合でも、hidden で POST はされる
     */
    isShowAddress: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    'update:companyName',
    'update:corporateAddress1',
    'update:industry',
    'update:scale',
    'update:listingStatus',
    'update:profession',
    'update:professionDomain',
    'open:professionDomain',
    'close:professionDomain',
    'update:positionLevel',
    'update:departmentAndPosition',
    'update:workMailAddress',
    'update:phoneNumber',
    'select:companyLeadInfo',
    'reset:corporateZipcode',
    'error'
  ],

  setup(props, { emit }) {
    /**
     * レンダリング領域が広い場合において、フルカラムで表示する要素と、マルチカラム（2 カラムのうちの 1 カラム使用）で表示する要素の管理情報
     */
    const colSizeInfo = computed<Record<string, 'full' | 'multi'>>(() => {
      const {
        companyName,
        corporateAddress1,
        industry,
        scale,
        listingStatus,
        profession,
        positionLevel,
        departmentAndPosition,
        workMailAddress,
        phoneNumber,
      } = props.userLeadInfo

      const sizeInfo: Record<string, 'full' | 'multi'> = {}

      if (companyName)                              Object.assign(sizeInfo, { companyName          : 'full' })
      if (corporateAddress1 && props.isShowAddress) Object.assign(sizeInfo, { corporateAddress1    : 'full' })
      if (industry)                                 Object.assign(sizeInfo, { industry             : 'multi' })
      if (scale)                                    Object.assign(sizeInfo, { scale                : 'multi' })
      if (listingStatus && listingStatus.name)      Object.assign(sizeInfo, { listingStatus        : 'multi' })
      if (profession)                               Object.assign(sizeInfo, { profession           : 'multi' })
      if (positionLevel)                            Object.assign(sizeInfo, { positionLevel        : 'multi' })
      if (departmentAndPosition)                    Object.assign(sizeInfo, { departmentAndPosition: 'multi' })
      if (workMailAddress)                          Object.assign(sizeInfo, { workMailAddress      : 'full' })
      if (phoneNumber)                              Object.assign(sizeInfo, { phoneNumber          : 'full' })

      const multiColumnItems = Object.fromEntries(
        Object.entries(sizeInfo).filter(([_, value]) => value === 'multi')
      )

      const multiColumnKeys = Object.keys(multiColumnItems)

      // マルチカラムに設定されている要素の数が奇数の場合、
      // 最後の要素がハーフカラムで段落ちする状態になってしまうため、
      // その場合は最後の要素はフルカラムにして全体のデザイン性を担保する
      if (multiColumnKeys.length % 2 === 1) {
        const colEntries = Object.entries(sizeInfo)
        const [lastKey] = colEntries[colEntries.length - 1]
        sizeInfo[lastKey] = 'full'
      }

      return sizeInfo
    })

    /**
     * 会社名入力に必要な props オブジェクトが渡ってきているかどうか
     */
    const hasCompanyNameInfo = computed<boolean>(() => {
      return !!(
        props.userLeadInfo.companyName &&
        props.userLeadInfo.corporateZipcode &&
        props.userLeadInfo.corporateAddress1 &&
        props.userLeadInfo.corporateAddress2 &&
        props.userLeadInfo.corporateNumber &&
        props.userLeadInfo.corporateEmployeeNum)
    })

    onMounted(() => {
      // マウントされた郵便番号の初期値が半角数字以外の文字列が含まれていた場合、強制的にブランクにして更新する
      // ※ 古い会員の場合、まだ半角数字のみのバリデーション制御がなかったため、「-（ハイフン）」つきの郵便番号文字列で登録しているようなユーザもいるため、そういった古いデータは一旦リセットかける
      if (props.userLeadInfo.corporateZipcode && !isValidNumber(props.userLeadInfo.corporateZipcode.value)) {
        emit('reset:corporateZipcode', '')
      }
    })

    return {
      // computed
      colSizeInfo,
      hasCompanyNameInfo,
    }
  }
})
</script>

<template>
  <div class="div">
    <div class="o-list__careerProfile">
      <ul class="o-list__careerProfile--items">
        <li
          v-if="userLeadInfo.companyName && hasCompanyNameInfo"
          class="o-list__careerProfile--item"
          :data-full-col="colSizeInfo['companyName'] === 'full'">
          <InputCompanyName
            v-if="userLeadInfo.companyName"
            :model-value="userLeadInfo.companyName.value"
            :corporate-zipcode="userLeadInfo.corporateZipcode!"
            :corporate-address1="userLeadInfo.corporateAddress1!"
            :corporate-address2="userLeadInfo.corporateAddress2!"
            :corporate-number="userLeadInfo.corporateNumber!"
            :corporate-employee-num="userLeadInfo.corporateEmployeeNum!"
            :namespace="namespace"
            :name="userLeadInfo.companyName.name"
            :validation-scheme="userLeadInfo.companyName.validationScheme"
            @update:model-value="$emit('update:companyName', $event)"
            @select:company-lead-info="$emit('select:companyLeadInfo', $event)" />
        </li>
        <li
          v-if="userLeadInfo.corporateAddress1 && isShowAddress"
          class="o-list__careerProfile--item"
          :data-full-col="colSizeInfo['corporateAddress1'] === 'full'">
          <InputForm
            :model-value="userLeadInfo.corporateAddress1.value"
            :namespace="namespace"
            :name="userLeadInfo.corporateAddress1.name"
            header="所在地"
            autocomplete="street-address"
            :validation-scheme="userLeadInfo.corporateAddress1.validationScheme"
            @update:model-value="$emit('update:corporateAddress1', $event)" />
        </li>
        <li
          v-if="userLeadInfo.industry"
          class="o-list__careerProfile--item"
          :data-full-col="colSizeInfo['industry'] === 'full'">
          <SelectorIndustry
            :industry="userLeadInfo.industry"
            :namespace="namespace"
            @update:industry="$emit('update:industry', $event)" />
        </li>
        <li
          v-if="userLeadInfo.scale"
          class="o-list__careerProfile--item"
          :data-full-col="colSizeInfo['scale'] === 'full'">
          <SelectorScale
            :scale="userLeadInfo.scale"
            :namespace="namespace"
            @update:scale="$emit('update:scale', $event)" />
        </li>
        <li
          v-if="userLeadInfo.listingStatus && userLeadInfo.listingStatus.name"
          class="o-list__careerProfile--item"
          :data-full-col="colSizeInfo['listingStatus'] === 'full'">
          <SelectorListingStatus
            :listing-status="userLeadInfo.listingStatus"
            :namespace="namespace"
            @update:listing-status="$emit('update:listingStatus', $event)" />
        </li>
        <li
          v-if="userLeadInfo.profession"
          class="o-list__careerProfile--item"
          :data-full-col="colSizeInfo['profession'] === 'full'">
          <SelectorProfessionWithDomain
            v-if="userLeadInfo.professionDomain"
            :profession="userLeadInfo.profession"
            :profession-domain="userLeadInfo.professionDomain"
            :namespace="namespace"
            @update:profession="$emit('update:profession', $event)"
            @update:profession-domain="$emit('update:professionDomain', $event)"
            @open:profession-domain="$emit('open:professionDomain')"
            @close:profession-domain="$emit('close:professionDomain')" />
          <SelectorProfession
            v-else
            :profession="userLeadInfo.profession"
            :namespace="namespace"
            @update:profession="$emit('update:profession', $event)" />
        </li>
        <li
          v-if="userLeadInfo.positionLevel"
          class="o-list__careerProfile--item"
          :data-full-col="colSizeInfo['positionLevel'] === 'full'">
          <SelectorPositionLevel
            :position-level="userLeadInfo.positionLevel"
            :namespace="namespace"
            @update:position-level="$emit('update:positionLevel', $event)" />
        </li>
        <li
          v-if="userLeadInfo.departmentAndPosition"
          class="o-list__careerProfile--item"
          :data-full-col="colSizeInfo['departmentAndPosition'] === 'full'">
          <InputFormDepartmentAndPosition
            :department-and-position="userLeadInfo.departmentAndPosition"
            :namespace="namespace"
            @update:model-value="$emit('update:departmentAndPosition', $event)" />
        </li>

        <!-- 勤務先メールアドレス -->
        <li
          v-if="userLeadInfo.workMailAddress"
          class="o-list__careerProfile--item"
          :data-full-col="true">
          <InputFormWorkMailAddress
            :model-value="userLeadInfo.workMailAddress.value"
            :namespace="namespace"
            :name="userLeadInfo.workMailAddress.name"
            :validation-scheme="userLeadInfo.workMailAddress.validationScheme"
            @update:model-value="$emit('update:workMailAddress', $event)" />
        </li>

        <!-- 電話番号 -->
        <li
          v-if="userLeadInfo.phoneNumber"
          class="o-list__careerProfile--item"
          :data-full-col="true">
          <InputFormPhoneNumber
            :model-value="userLeadInfo.phoneNumber.value"
            :namespace="namespace"
            :name="userLeadInfo.phoneNumber.name"
            :validation-scheme="userLeadInfo.phoneNumber.validationScheme"
            @update:model-value="$emit('update:phoneNumber', $event)" />
        </li>
      </ul>
    <!-- /.o-list__careerProfile --></div>

    <input
      v-if="userLeadInfo.careerId"
      class="input"
      type="hidden"
      :name="userLeadInfo.careerId.name"
      :value="userLeadInfo.careerId.value">
  </div>
</template>

<style lang="sass" scoped>
// layout
.o-list__careerProfile
  $root: &
  $margin-size-px: 20
  $margin-em: math.div($margin-size-px, $font-normal-px)
  $margin-percent: getRelativeValue($margin-size-px, $global-contents-width-pc)
  margin: #{$margin-em}em auto 0
  container-type: inline-size

  &:first-child
    margin-top: 0

  & &--items
    display: grid
    grid-template-columns: repeat(2, 1fr)
    column-gap: $margin-percent
    row-gap: #{$margin-em}em
    position: relative  // for iOS z-index bug (「職種」が「人事」選択時に表示される「担当領域」のマルチセレクター展開時に、後続要素（勤務先メールアドレス等）の下にレイヤーが裏回るのを防止)

  & &--item
    grid-column: span 1
    position: relative  // for iOS z-index bug

    &[data-full-col="true"]
      grid-column: 1 / 3

  +container(480px)
    #{$root}--items
      display: block

    #{$root}--item
      margin-top: #{$margin-em}em

      &:first-child
        margin-top: 0
</style>
